import React, { useMemo } from 'react';
import Input from '../../../components/ui/Input';
import Select, { type SelectChangeEvent } from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { useListPropertiesQuery } from '../../../store/api/propertiesApi';

interface TenantFiltersProps {
	searchTerm: string;
	setSearchTerm: (value: string) => void;
	propertyFilter: string;
	setPropertyFilter: (value: string) => void;
	statusFilter: string;
	setStatusFilter: (value: string) => void;
	paymentFilter: string;
	setPaymentFilter: (value: string) => void;
	onClearFilters: () => void;
}

const TenantFilters: React.FC<TenantFiltersProps> = ({
	searchTerm,
	setSearchTerm,
	propertyFilter,
	setPropertyFilter,
	statusFilter,
	setStatusFilter,
	paymentFilter,
	setPaymentFilter,
	onClearFilters
}) => {
  // Fetch properties from API
  const { data: propertiesData } = useListPropertiesQuery({ page_size: 1000 });

  // Build property options from API data
  const propertyOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Properties' }];
    if (propertiesData?.results) {
      propertiesData.results.forEach((property) => {
        options.push({
          value: property.id,
          label: `${property.name} - ${property.address}`,
        });
      });
    }
    return options;
  }, [propertiesData]);

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
    { value: 'terminated', label: 'Terminated' }
  ];

  const paymentOptions = [
    { value: 'all', label: 'All Payment Status' },
    { value: 'current', label: 'Current' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'partial', label: 'Partial Payment' },
    { value: 'pending', label: 'Pending' }
  ];

  const handlePropertyChange = (event: SelectChangeEvent): void => {
    setPropertyFilter(event.target.value as string);
  };

  const handleStatusChange = (event: SelectChangeEvent): void => {
    setStatusFilter(event.target.value as string);
  };

  const handlePaymentChange = (event: SelectChangeEvent): void => {
    setPaymentFilter(event.target.value as string);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Search & Filters</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          iconName="X"
          iconPosition="left"
        >
          Clear All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Input
          type="search"
          placeholder="Search tenants..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />

        <Select
          placeholder="Select property"
          options={propertyOptions}
          value={propertyFilter}
          onChange={handlePropertyChange}
        />

        <Select
          placeholder="Tenant status"
          options={statusOptions}
          value={statusFilter}
          onChange={handleStatusChange}
        />

        <Select
          placeholder="Payment status"
          options={paymentOptions}
          value={paymentFilter}
          onChange={handlePaymentChange}
        />
      </div>
    </div>
  );
};

export default TenantFilters;
